import { BehaviorSubject } from 'rxjs';
import { Modes } from "./mode";
import { ViewModes } from "./viewmodes";
import { VisualizationMode } from 'src/app/template/menu/menu.service';
import * as THREE from '@phdsoft/three';

export type ActionType = 'Select' | 'Hide';
export class AssetState {

    public regions: { [id: string]: { visible?: boolean, sections?: { [id: string]: boolean } } };
    public regionId: string;

    public sectionId: string;
    public sections: Array<{ id: string, visible: boolean }>;

    public elementId: string;

    public box3: THREE.Box3;
    public position: THREE.Vector3;
    public target: THREE.Vector3;

    // Full camera state (Phase 2 -- CAPT-01)
    public cameraUp: THREE.Vector3;
    public cameraFov: number;
    public cameraZoom: number;
    public cameraNear: number;
    public cameraFar: number;
    public cameraAspect: number;

    public object: THREE.Object3D;
    public objectVisibility: boolean;

    public mode: Modes;
    public viewMode: ViewModes;
    public visualizationMode: VisualizationMode;
    public action: ActionType;
    public isRoot: boolean;

    /**
     * Compares two regions from the AssetState class
     * @param region1 First region to compare
     * @param region2 Second region to compare
     * @returns true if regions are identical (same keys, visibility and sections), false otherwise
     */
    public static compareRegions(region1: { [id: string]: { visible?: boolean, sections?: { [id: string]: boolean } } }, 
                               region2: { [id: string]: { visible?: boolean, sections?: { [id: string]: boolean } } }): boolean {
        // Check if both regions are null/undefined
        if (!region1 && !region2) return true;
        if (!region1 || !region2) return false;

        // Get all keys from both regions
        const keys1 = Object.keys(region1);
        const keys2 = Object.keys(region2);

        // Check if number of keys match
        if (keys1.length !== keys2.length) return false;

        // Compare each key and its values
        for (const key of keys1) {
            // Check if key exists in both regions
            if (!region2[key]) return false;

            // Compare visibility
            if (region1[key].visible !== region2[key].visible) return false;

            // Compare sections if they exist
            if (region1[key].sections || region2[key].sections) {
                // If one has sections and the other doesn't
                if (!region1[key].sections || !region2[key].sections) return false;

                const sections1 = region1[key].sections;
                const sections2 = region2[key].sections;

                // Compare section keys
                const sectionKeys1 = Object.keys(sections1);
                const sectionKeys2 = Object.keys(sections2);

                if (sectionKeys1.length !== sectionKeys2.length) return false;

                // Compare each section's visibility
                for (const sectionKey of sectionKeys1) {
                    if (sections1[sectionKey] !== sections2[sectionKey]) return false;
                }
            }
        }

        return true;
    }
}

export class AssetStateList {
    public stateChanged = new BehaviorSubject<boolean>(false);

    private _currentStateIndex: number;

    private readonly _maxSize: number;

    private _assetStateList: Array<AssetState> = new Array<AssetState>();

    // -----------------------------------------------------------------------
    // Constructor
    // -----------------------------------------------------------------------

    constructor(maxSize: number = 100) {
        this._maxSize = maxSize;
        this._currentStateIndex = 0;
        const assetStateRoot: AssetState = new AssetState();
        assetStateRoot.isRoot = true;
        this._assetStateList.push(assetStateRoot);
    }

    // -----------------------------------------------------------------------
    // Public read-only getters
    // -----------------------------------------------------------------------

    /**
     * Returns the state at the current cursor position.
     * @returns The current AssetState, or null if the cursor is out of bounds.
     */
    public get currentState(): AssetState | null {
        if (this._currentStateIndex > -1 && this._currentStateIndex < this._assetStateList.length) {
            return this._assetStateList[this._currentStateIndex];
        }
        return null;
    }

    /**
     * Whether an undo operation is possible (cursor is not at the beginning).
     * @returns true if moveToPrevious() would succeed.
     */
    public get canUndo(): boolean {
        return this._currentStateIndex > 0;
    }

    /**
     * Whether a redo operation is possible (cursor is not at the end).
     * @returns true if moveToNext() would succeed.
     */
    public get canRedo(): boolean {
        return this._currentStateIndex < this._assetStateList.length - 1;
    }

    // -----------------------------------------------------------------------
    // Public peek methods (side-effect-free)
    // -----------------------------------------------------------------------

    /**
     * Returns the state before the cursor without moving the cursor.
     * @returns The previous AssetState, or null if cursor is at the beginning.
     */
    public peekPrevious(): AssetState | null {
        if (this._currentStateIndex > 0) {
            return this._assetStateList[this._currentStateIndex - 1];
        }
        return null;
    }

    /**
     * Returns the state after the cursor without moving the cursor.
     * @returns The next AssetState, or null if cursor is at the end.
     */
    public peekNext(): AssetState | null {
        if (this._currentStateIndex < this._assetStateList.length - 1) {
            return this._assetStateList[this._currentStateIndex + 1];
        }
        return null;
    }

    // -----------------------------------------------------------------------
    // Public move methods (explicit cursor movement)
    // -----------------------------------------------------------------------

    /**
     * Moves the cursor one position back and returns the target state.
     * Does NOT emit on stateChanged -- emission is the caller's
     * responsibility (via syncUI).
     * @returns The previous AssetState, or null if already at the beginning.
     */
    public moveToPrevious(): AssetState | null {
        if (!this.canUndo) {
            return null;
        }
        this._currentStateIndex--;
        const state = this._assetStateList[this._currentStateIndex];
        return state;
    }

    /**
     * Moves the cursor one position forward and returns the target state.
     * Does NOT emit on stateChanged -- emission is the caller's
     * responsibility (via syncUI).
     * @returns The next AssetState, or null if already at the end.
     */
    public moveToNext(): AssetState | null {
        if (!this.canRedo) {
            return null;
        }
        this._currentStateIndex++;
        const state = this._assetStateList[this._currentStateIndex];
        return state;
    }

    // -----------------------------------------------------------------------
    // Public mutation
    // -----------------------------------------------------------------------

    /** > 0 while withoutRecording() runs (nestable). */
    private recordingSuspendDepth = 0;

    /**
     * Runs `fn` without adding undo/redo entries: for view changes made by a tool rather than by the
     * user's navigation (e.g. the inspection-retarget region switch). Synchronous scope only: entries
     * pushed after `fn` returns, including from async work it started, are recorded normally.
     * `stateChanged` still fires so listeners that follow the visible regions (FEA, load cycle) keep up;
     * the redo tail is left untouched.
     */
    public withoutRecording<T>(fn: () => T): T {
        this.recordingSuspendDepth++;
        try {
            return fn();
        } finally {
            this.recordingSuspendDepth--;
        }
    }

    /**
     * Adds a new state after the current cursor position, truncating any
     * forward (redo) history. After undo, adding a new state discards all
     * states ahead of the cursor (traditional undo behavior).
     * Enforces the configured maximum size by evicting the oldest entries
     * when exceeded. Emits on stateChanged after insertion and eviction.
     * @param assetState The new state to add.
     */
    public addAssetState(assetState: AssetState): void {
        if (this.recordingSuspendDepth > 0) {
            this.stateChanged.next(true);
            return;
        }
        // PHASE 4: Truncate forward history (traditional undo behavior).
        // Per user decision, overrides Phase 1 splice-insertion approach.
        // After undo, adding a new state discards all forward (redo) history.
        if (this._currentStateIndex < this._assetStateList.length - 1) {
            this._assetStateList.splice(this._currentStateIndex + 1);
        }
        this._assetStateList.push(assetState);
        this._currentStateIndex = this._assetStateList.length - 1;

        // Evict the oldest entries if over limit (HIST-05). Index 0 is the root
        // (default view) and must survive, otherwise undo can never get home.
        while (this._assetStateList.length > this._maxSize) {
            this._assetStateList.splice(1, 1);
            this._currentStateIndex = Math.max(0, this._currentStateIndex - 1);
        }

        this.stateChanged.next(true);
    }

    // -----------------------------------------------------------------------
    // Backward-compatible / deprecated methods
    // -----------------------------------------------------------------------

    /**
     * @deprecated Use currentState instead -- will be removed in Phase 4.
     */
    public get getCurrentState(): AssetState {
        return this.currentState;
    }

    /**
     * @deprecated Use moveToPrevious() instead -- will be removed in Phase 4.
     */
    public get previous(): AssetState {
        return this.moveToPrevious();
    }

    /**
     * @deprecated Use moveToNext() instead -- will be removed in Phase 4.
     */
    public get next(): AssetState {
        return this.moveToNext();
    }

    public getCurrentStateIndex(): number {
        return this._currentStateIndex;
    }

    /** @deprecated Use moveToPrevious/moveToNext for cursor control. */
    public setCurrentStateIndex(index: number): void {
        this._currentStateIndex = index;
    }

    public get assetStateList(): Array<AssetState> {
        return this._assetStateList;
    }

    /** @deprecated Direct array assignment is discouraged. */
    public set assetStateList(v: Array<AssetState>) {
        this._assetStateList = v;
    }

    public removeStatesAtIndices(indices: number[]): void {
        // Sort indices in descending order to remove from highest to lowest
        indices.sort((a, b) => b - a);
        for (const index of indices) {
            if (index >= 0 && index < this._assetStateList.length) {
                this._assetStateList.splice(index, 1);
                // Adjust _currentStateIndex if necessary
                if (this._currentStateIndex >= index) {
                    this._currentStateIndex = Math.max(0, this._currentStateIndex - 1);
                }
            }
        }
        this.stateChanged.next(true);
    }
}
